/**  **Note:** (Vishal Barnawal) This file is a copy of redux-api-v2 because:
 * We needed to adjust certain options for making calls to the referral portal.
 * We are currently integrating the referral portal with our referralv2 app, and the API options will differ from those of redux API.
 */
import type {Dispatch, ThunkAction, GetState, State} from 'src/reducers';

import {
  RawQuery,
  ApiOptions,
  RequestOptions,
  ApiPostOptions,
  base,
} from './redux-api';
import {USER_AUTH} from 'src/action-creators/referrer-portal/authentication';

import {ApiError} from 'src/utils/errors';
import {pushModal, popModal} from 'src/action-creators/modal';
import {setPostAuthPath} from 'src/action-creators/accounts';
import {batch} from 'src/action-creators/batch';
import {browserHistory} from 'src/reroutes/browser-history';


export type ReferrerPortalOptions = {
  apiPath: string;
};

const referrerPortalOptions = {
  apiPath: '/referrals/api/v1/',
} as const;

export const API_ERROR = 'api/apiError';

export const get = (
  path: string,
  query?: RawQuery,
  options?: ApiOptions,
): ThunkAction<any> =>
  baseRequest(path, {
    method: 'GET',
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'query' does not exist in type 'RequestInit'.
    query,
    options: {...options, ...referrerPortalOptions},
  });

export const post = (
  path: string,
  data?: Record<any, any>,
  query?: RawQuery,
  options?: ApiPostOptions,
): ThunkAction<any> =>
  baseRequest(path, {
    method: 'POST',
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'data' does not exist in type 'RequestInit'.
    data,
    query,
    options: {...options, ...referrerPortalOptions},
  });

export const patch = (
  path: string,
  data?: Record<any, any>,
  query?: RawQuery,
  options?: ApiPostOptions,
): ThunkAction<any> =>
  baseRequest(path, {
    method: 'PATCH',
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'data' does not exist in type 'RequestInit'.
    data,
    query,
    options: {...options, ...referrerPortalOptions},
  });

export const put = (
  path: string,
  data?: Record<any, any>,
  query?: RawQuery,
  options?: ApiPostOptions,
): ThunkAction<any> =>
  baseRequest(path, {
    method: 'PUT',
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'data' does not exist in type 'RequestInit'.
    data,
    query,
    options: {...options, ...referrerPortalOptions},
  });

export const del = (
  path: string,
  data?: Record<any, any>,
  options?: ApiOptions,
): ThunkAction<any> =>
  baseRequest(path, {
    method: 'DELETE',
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'data' does not exist in type 'RequestInit'.
    data,
    options: {...options, ...referrerPortalOptions},
  });

const baseRequest =
  (path: string, options?: RequestInit): ThunkAction<unknown> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const env = getState().env;
    const splitDomain = env?.hostname?.split('.');
    const subDomain = splitDomain[0];
    // The server for the referrer portal and the referral system are separate,
    // so we cannot create a single API that retrieves system details from the backend.
    // That's why we check the subdomain to verify whether the API call is coming from the referral app or the Sense app.
    const isReferrerPortalApp = subDomain === 'referrals';
    const senseApiPath = '/api/v1/referral/';
    const referrerPortalApiPath = '/referrals/api/v1/';
    const apiPath = isReferrerPortalApp ? referrerPortalApiPath : senseApiPath;

    const baseOptions = {
      ...options,
      options: {
        // @ts-expect-error - TS2339 - Property 'options' does not exist on type 'RequestInit'.
        ...options?.options,
        apiPath,
      },
    } as const;

    const isReferrerPortalUserAuthed = (state: State) =>
      state?.referrerPortal?.isAuthed;

    const additionalParams = isReferrerPortalApp
      ? [isReferrerPortalUserAuthed, showReferrerPortalLoginModal]
      : [];

    return dispatch(base(path, baseOptions, ...additionalParams)).then(
      async (response: Response) => {
        const text = await response.text();

        let json;
        if (response.status !== 404) {
          try {
            json = JSON.parse(text);
          } catch (error: any) {
            json = text;
          }
        } else {
          json = text;
        }

        if (!response.ok) {
          throw {
            error: new Error('Bad Request'),
            response: json,
            // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'path' does not exist in type 'RequestInit'.
            apiError: new ApiError({path, ...baseOptions}, response, json),
          };
        }
        return json;
      },
    );
  };

const showReferrerPortalLoginModal = () => (dispatch: any) =>
  dispatch(
    pushModal({
      type: 'GENERIC_ERROR',
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'title' does not exist in type 'BasicModal'.
      title: 'Expired Session',
      text: 'Looks like your session expired. Please sign back in.',
      confirmText: 'Okay',
      handleConfirm: () => {
        dispatch(
          batch(
            popModal(),
            dispatch({type: USER_AUTH, payload: false}),

            setPostAuthPath(window.location.pathname),
          ),
        );
        browserHistory.push('/signin');
      },
      handleAbort: () => {
        dispatch(popModal());
      },
    }),
  );
